/**
 * NarrativeInputHook - Auto-resizing textarea for lead narrative
 *
 * Features:
 * - Auto-expands/shrinks as user types (up to max-height of 200px)
 * - Maintains minimum height for comfortable typing
 * - Preserves content on LiveView updates
 */
const NarrativeInputHook = {
  mounted() {
    this.handleInput = () => {
      this.lastValue = this.el.value;
      this.autosize();
    };

    this.setupAutosize();
  },

  updated() {
    const isFocused = document.activeElement === this.el;
    const valueUnchanged = this.el.value === this.lastValue;

    // LiveView patches can arrive after each debounced keystroke even when the
    // textarea value is unchanged. Re-running autosize while focused causes the
    // viewport to jump on mobile, so let the local input handler own sizing.
    if (isFocused && valueUnchanged) {
      return;
    }

    this.lastValue = this.el.value;
    this.autosize();
  },

  destroyed() {
    this.el.removeEventListener("input", this.handleInput);
  },

  setupAutosize() {
    this.el.style.boxSizing = "border-box";
    this.offset = this.el.offsetHeight - this.el.clientHeight || 0;
    this.lastValue = this.el.value;

    // Listen for input events
    this.el.addEventListener("input", this.handleInput);

    // Initial sizing
    this.autosize();
  },

  autosize() {
    const maxHeight = parseInt(getComputedStyle(this.el).maxHeight) || 200;
    const minHeight = parseInt(getComputedStyle(this.el).minHeight) || 80;

    // Prevent visual flash: hide overflow, collapse to measure, then set final
    // height — all synchronous so the browser only paints the final state.
    const prevOverflow = this.el.style.overflow;
    this.el.style.overflow = "hidden";
    this.el.style.height = "0px";

    const scrollHeight = this.el.scrollHeight + this.offset;
    const newHeight = Math.max(minHeight, Math.min(scrollHeight, maxHeight));

    this.el.style.height = newHeight + "px";
    this.el.style.overflow = prevOverflow;
  }
};

export default NarrativeInputHook;
